import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select,
  Stack, Typography,
} from '@mui/material';
import { notifyPermissionError, useNotification, useOptionalPermissionCenter } from '@1823-partners/core';

import {
  fetchRemittancePeriods,
  fetchRemittanceRunLoans,
  fetchRemittanceRuns,
  fetchRemittanceRunState,
  triggerRemittanceRun,
} from '../../api/remittance';
import RunHistoryGrid from './RunHistoryGrid';
import ReconciliationGrid from './ReconciliationGrid';
import ExcludedInputs from './ExcludedInputs';
import RemittanceUploadPanel from './RemittanceUploadPanel';
import { unavailableReason } from '../../config/backendFeatures';
import { deadWorker } from './remittanceLease';

/** CML remittance history and immutable, asynchronous run creation. */
const POLL_INTERVAL_MS = 5000;
const POLL_REQUEST_TIMEOUT_MS = 10000;
const MAX_POLL_FAILURES = 6;
const MAX_POLL_DURATION_MS = 31 * 60 * 1000;
const FAILURE_STATES = ['failed', 'timeout'];
// Every state the server writes to a durable claim while it still intends to run it. `accepted`
// belongs here: the row exists before any worker has taken it, and a claim stuck there is the
// failure this file could not previously see.
const IN_FLIGHT_STATES = ['accepted', 'syncing', 'running'];
const PENDING_RUN_STORAGE_KEY = 'recon.remittance.pendingRun.v2';

export const REMITTANCE_SELECTOR_GRID_AREAS = Object.freeze({
  xs: '"filesToggle" "runsToggle" "filesContent" "runsContent"',
  md: '"filesToggle runsToggle" "filesContent filesContent" "runsContent runsContent"',
});

const createLaunchId = () => window.crypto.randomUUID().replace(/-/g, '');

const withTimeout = (request, timeoutMs) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('request timed out')), timeoutMs);
  });
  return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
};

const readPendingRun = () => {
  try {
    const value = window.sessionStorage.getItem(PENDING_RUN_STORAGE_KEY);
    const pending = value ? JSON.parse(value) : null;
    return pending?.accepted && pending?.launchId ? pending : null;
  } catch (err) {
    return null;
  }
};

const persistPendingRun = (pending) => {
  try {
    window.sessionStorage.setItem(PENDING_RUN_STORAGE_KEY, JSON.stringify(pending));
  } catch (err) {
    return false;
  }
  return true;
};

const clearPendingRun = (expectedLaunchId) => {
  try {
    if (expectedLaunchId) {
      const value = window.sessionStorage.getItem(PENDING_RUN_STORAGE_KEY);
      const pending = value ? JSON.parse(value) : null;
      if (pending?.launchId !== expectedLaunchId) return false;
    }
    window.sessionStorage.removeItem(PENDING_RUN_STORAGE_KEY);
  } catch (err) {
    return false;
  }
  return true;
};

const findHistoryCandidates = (rows, pending) =>
  rows.filter((row) => row.RUN_ID === pending.launchId);

const findDuplicateRunIds = (rows) => {
  const seen = new Set();
  const duplicates = new Set();
  rows.forEach((row) => {
    if (!row.RUN_ID) return;
    if (seen.has(row.RUN_ID)) duplicates.add(row.RUN_ID);
    seen.add(row.RUN_ID);
  });
  return duplicates;
};

const duplicateHistoryMessage = (runIds) => {
  const singular = runIds.size === 1;
  const label = singular ? 'Duplicate run id' : 'Duplicate run ids';
  const verb = singular ? 'was' : 'were';
  return label + ' ' + Array.from(runIds).join(', ') + ' ' + verb
    + ' hidden because the loan results cannot be distinguished.';
};

const isDefinitiveRejection = (err) => {
  const status = err?.response?.status;
  return status >= 400 && status < 500 && status !== 408;
};
// A mapping refusal is an expected operational state, not a crash: the run path derives its
// mapping from CRE and stops rather than reconciling a period whose mapping failed a safety check.
// The server names the loans in `refusals`, and showing only `message` sends the reader to the
// server log to find out which -- for the one error that stops a month's close.
const MAX_REFUSALS_SHOWN = 3;

export const describeRunRejection = (err, period) => {
  const data = err?.response?.data;
  const refusals = Array.isArray(data?.refusals) ? data.refusals.filter(Boolean) : [];
  if (data?.error === 'mapping_refused' && refusals.length) {
    const shown = refusals.slice(0, MAX_REFUSALS_SHOWN).join(' | ');
    const more =
      refusals.length > MAX_REFUSALS_SHOWN
        ? ` (+${refusals.length - MAX_REFUSALS_SHOWN} more)`
        : '';
    return `${period}: the runtime mapping was refused. ${shown}${more}`;
  }
  return data?.message || `Could not start a run for ${period}.`;
};

const RemittanceDashboard = ({ height = 900, initialRunId = null }) => {
  const { showNotification } = useNotification();
  const permissionCenter = useOptionalPermissionCenter();
  const canRun = !permissionCenter || permissionCenter.loading || permissionCenter.error
    ? true : permissionCenter.hasPermission('recon.remittance:run');
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  // Rows are stored WITH the run they belong to, and the rendered set is derived by matching that
  // against the current selection. Holding a bare array let the previous run's loans stay on screen
  // while the new request was in flight — and because `period` updates synchronously with the
  // selection, an export during that window wrote a file named for June containing May's rows.
  // Deriving makes a mismatch unrepresentable rather than merely unlikely.
  const [loanData, setLoanData] = useState({ runId: null, rows: [] });
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [error, setError] = useState(null);
  const [historyIntegrityIssue, setHistoryIntegrityIssue] = useState(null);
  const [deepLinkIssue, setDeepLinkIssue] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [periodsError, setPeriodsError] = useState(null);
  const [period, setPeriod] = useState('');
  const [pending, setPending] = useState(readPendingRun);
  const [pollPaused, setPollPaused] = useState(false);
  const [pollIssue, setPollIssue] = useState(null);
  const activeLaunchId = useRef(pending?.launchId || null);
  const mounted = useRef(false);
  const loadRunsRequest = useRef(0);
  const loadPeriodsRequest = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadRuns = useCallback(async ({ selectNewest = true } = {}) => {
    const requestId = ++loadRunsRequest.current;
    try {
      setLoadingRuns(true);
      setError(null);
      const [response, linkedResponse] = await Promise.all([
        fetchRemittanceRuns(),
        initialRunId ? fetchRemittanceRuns({ runId: initialRunId }) : Promise.resolve(null),
      ]);
      if (requestId !== loadRunsRequest.current) return;
      const historyRows = Array.isArray(response.data) ? response.data : [];
      const linkedRows = Array.isArray(linkedResponse?.data) ? linkedResponse.data : [];
      const historyIds = new Set(historyRows.map((row) => row.RUN_ID));
      const rows = [
        ...historyRows,
        ...linkedRows.filter((row) => !historyIds.has(row.RUN_ID)),
      ];
      const duplicateIds = findDuplicateRunIds(rows);
      const safeRows = rows.filter((row) => !duplicateIds.has(row.RUN_ID));
      const linkedRun = initialRunId
        ? safeRows.find((row) => row.RUN_ID === initialRunId)
        : null;
      setRuns(safeRows);
      setHistoryIntegrityIssue(
        duplicateIds.size ? duplicateHistoryMessage(duplicateIds) : null,
      );
      setDeepLinkIssue(
        initialRunId && !linkedRun
          ? `The linked run ${initialRunId} was not found. Showing the newest available run.`
          : null,
      );
      setSelected((current) => {
        if (current && !duplicateIds.has(current.RUN_ID)) return current;
        if (linkedRun) return linkedRun;
        return selectNewest ? safeRows[0] || null : null;
      });
    } catch (err) {
      if (requestId !== loadRunsRequest.current) return;
      console.error('Error loading remittance runs:', err);
      setError('Could not reach the remittance history service.');
    } finally {
      if (requestId === loadRunsRequest.current) setLoadingRuns(false);
    }
  }, [initialRunId]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  /**
   * Refresh the runnable months.
   *
   * *prefer* is the month just uploaded to: it did not exist in this list a moment ago, and
   * selecting it is what makes upload and run one motion.
   *
   * The type check is not defensive padding. The obvious tidy-up of the Retry button below is
   * `onClick={loadPeriods}`, which hands this a SyntheticEvent as *prefer*; without the check a
   * truthy non-month would reach `available.includes` and, on a bad day, repoint the selector.
   */
  const loadPeriods = useCallback(async (prefer) => {
    const preferred = typeof prefer === 'string' ? prefer : null;
    const requestId = ++loadPeriodsRequest.current;
    try {
      setLoadingPeriods(true);
      setPeriodsError(null);
      const response = await fetchRemittancePeriods();
      if (!mounted.current || requestId !== loadPeriodsRequest.current) return;
      const available = response.data?.periods ?? [];
      setPeriods(available);
      setPeriod((current) => {
        if (preferred && available.includes(preferred)) return preferred;
        return available.includes(current) ? current : available[0] || '';
      });
    } catch (err) {
      if (!mounted.current || requestId !== loadPeriodsRequest.current) return;
      console.error('Error loading remittance periods:', err);
      setPeriodsError('Could not list the months available to run.');
    } finally {
      if (mounted.current && requestId === loadPeriodsRequest.current) {
        setLoadingPeriods(false);
      }
    }
  }, []);

  useEffect(() => {
    loadPeriods();
    return () => {
      loadPeriodsRequest.current += 1;
    };
  }, [loadPeriods]);

  const runId = selected?.RUN_ID;
  const loans = loanData.runId === runId ? loanData.rows : [];

  useEffect(() => {
    if (!runId) {
      setLoanData({ runId: null, rows: [] });
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingLoans(true);
        const response = await fetchRemittanceRunLoans(runId);
        // Guard against a slow earlier response overwriting a newer selection.
        if (!cancelled) {
          setLoanData({ runId, rows: Array.isArray(response.data) ? response.data : [] });
        }
      } catch (err) {
        console.error('Error loading reconciliation:', err);
        if (!cancelled) {
          setLoanData({ runId, rows: [] });
          showNotification('Could not load this run’s reconciliation.', 'error');
        }
      } finally {
        if (!cancelled) setLoadingLoans(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [runId, showNotification]);

  const RUN_UNAVAILABLE = unavailableReason('remittanceRun');

  const handleRun = useCallback(async () => {
    if (!period || !canRun) return;
    const launchId = createLaunchId();
    const submittedRun = { period, accepted: true, launchId };
    setPollPaused(false);
    setPollIssue(null);
    clearPendingRun();
    activeLaunchId.current = launchId;
    setPending(submittedRun);
    persistPendingRun(submittedRun);

    try {
      await triggerRemittanceRun(period, launchId);
      if (!mounted.current || activeLaunchId.current !== launchId) return;
      showNotification(`Reconciliation started for ${period}`, 'info');
    } catch (err) {
      if (!mounted.current || activeLaunchId.current !== launchId) return;
      console.error('Error starting remittance run:', err);
      if (!isDefinitiveRejection(err)) {
        showNotification(
          `The request outcome for ${period} is unknown; checking run status.`,
          'warning',
        );
        return;
      }

      activeLaunchId.current = null;
      clearPendingRun(launchId);
      setPending((current) => (current?.launchId === launchId ? null : current));
      if (!notifyPermissionError(showNotification, err)) {
        showNotification(err?.unavailable ? err.message : describeRunRejection(err, period), 'error');
      }
    }
  }, [period, showNotification, canRun]);

  const handleAbandonWait = useCallback(() => {
    const launchId = activeLaunchId.current;
    activeLaunchId.current = null;
    clearPendingRun(launchId);
    setPending((current) => (current?.launchId === launchId ? null : current));
    setPollPaused(false);
    setPollIssue(null);
    showNotification('Stopped waiting for the remittance run.', 'warning');
  }, [showNotification]);

  useEffect(() => {
    if (!pending?.accepted || pollPaused) return undefined;
    let cancelled = false;
    let stopped = false;
    let failedPolls = 0;
    const deadline = Date.now() + MAX_POLL_DURATION_MS;

    const settle = (message, severity) => {
      if (cancelled || activeLaunchId.current !== pending.launchId) return;
      stopped = true;
      activeLaunchId.current = null;
      clearPendingRun(pending.launchId);
      setPending((current) => (
        current?.launchId === pending.launchId ? null : current
      ));
      setPollPaused(false);
      setPollIssue(null);
      showNotification(message, severity);
    };

    // `mayBeActive` false means the server itself told us no worker is behind this run, so the
    // usual "it may still be running" hedge would be wrong.
    const pausePolling = (message, mayBeActive = true) => {
      if (cancelled || activeLaunchId.current !== pending.launchId) return;
      stopped = true;
      setPollPaused(true);
      setPollIssue({ message, mayBeActive });
    };

    const noteFailedPoll = () => {
      failedPolls += 1;
      if (failedPolls >= MAX_POLL_FAILURES) {
        pausePolling('Could not confirm this run after several status checks.');
      }
    };

    const tick = async () => {
      if (activeLaunchId.current !== pending.launchId) return;
      if (Date.now() >= deadline) {
        pausePolling('The run did not reach a terminal state within 31 minutes.');
        return;
      }

      const [historyResult, stateResult] = await Promise.allSettled([
        withTimeout(
          fetchRemittanceRuns({ runId: pending.launchId, limit: 2 }),
          POLL_REQUEST_TIMEOUT_MS,
        ),
        withTimeout(
          fetchRemittanceRunState(pending.launchId),
          POLL_REQUEST_TIMEOUT_MS,
        ),
      ]);
      if (cancelled || activeLaunchId.current !== pending.launchId) return;

      if (historyResult.status === 'fulfilled') {
        const rows = Array.isArray(historyResult.value.data) ? historyResult.value.data : [];
        const candidates = findHistoryCandidates(rows, pending);
        if (candidates.length === 1) {
          const landed = candidates[0];
          setSelected(landed);
          setLoanData({ runId: null, rows: [] });
          settle(
            `Run ${landed.RUN_ID} finished for ${pending.period} (${landed.RUN_STATUS}).`,
            landed.RUN_STATUS === 'ok' ? 'success' : 'warning',
          );
          loadRuns({ selectNewest: false });
          return;
        }
        if (candidates.length > 1) {
          const duplicateIds = new Set(candidates.map((row) => row.RUN_ID));
          const message = duplicateHistoryMessage(duplicateIds);
          setHistoryIntegrityIssue(message);
          setSelected(null);
          setLoanData({ runId: null, rows: [] });
          settle(message, 'error');
          return;
        }
      } else {
        console.error('Error polling remittance history:', historyResult.reason);
      }

      let stateMatched = false;
      if (stateResult.status === 'fulfilled') {
        const stateResponse = stateResult.value;
        const state = stateResponse.data?.state;
        const sameWorker = stateResponse.data?.launch_id === pending.launchId;
        if (sameWorker && state) {
          stateMatched = true;
          failedPolls = 0;
          if (FAILURE_STATES.includes(state)) {
            const detail = stateResponse.data?.detail;
            setSelected(null);
            setLoanData({ runId: null, rows: [] });
            settle(
              `Run for ${pending.period} ended as ${state}${detail ? `: ${detail}` : ''}.`,
              'error',
            );
            loadRuns({ selectNewest: false });
          } else if (IN_FLIGHT_STATES.includes(state)) {
            // The server still calls this run live. Believe it only while its lease says a worker
            // is behind it, otherwise the wait outlives the run and the controls stay disabled.
            const dead = deadWorker(stateResponse.data);
            if (dead) {
              pausePolling(dead, false);
              return;
            }
          }
        }
      } else {
        console.error('Error polling remittance worker state:', stateResult.reason);
      }

      if (!stateMatched) noteFailedPoll();
    };

    let timer;
    const poll = async () => {
      await tick();
      if (!cancelled && !stopped) timer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pending, pollPaused, loadRuns, showNotification]);

  const integrity = useMemo(() => {
    if (!selected) return null;
    return {
      clean: selected.INTEGRITY_CLEAN && !selected.UNMATCHED_ROWS && !selected.UNRECOGNIZED_FILES,
      unrecognized: selected.UNRECOGNIZED_FILES || 0,
      unmatched: selected.UNMATCHED_ROWS || 0,
    };
  }, [selected]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
      {/*
        Retry, not just a message. Runs load once on mount, so a backend that was down when the tab
        mounted leaves this banner up until the whole page is reloaded — even after the backend
        recovers. Losing the grid to a transient outage is the wrong failure mode for a read-only
        view whose data is immutable and safe to re-request.
      */}
      {error && (
        <Alert
          severity="warning"
          action={(
            <Button color="inherit" size="small" onClick={() => loadRuns()} disabled={loadingRuns}>
              {loadingRuns ? 'Retrying…' : 'Retry'}
            </Button>
          )}
        >
          <AlertTitle>Backend unavailable</AlertTitle>
          {error} Runs are produced by the CLI; this tab only reads them.
        </Alert>
      )}

      {historyIntegrityIssue && (
        <Alert severity="error">
          <AlertTitle>Run history integrity error</AlertTitle>
          {historyIntegrityIssue}
        </Alert>
      )}

      {deepLinkIssue && (
        <Alert severity="warning">
          <AlertTitle>Linked run unavailable</AlertTitle>
          {deepLinkIssue}
        </Alert>
      )}

      {periodsError && (
        <Alert
          severity="warning"
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => loadPeriods()}
              disabled={loadingPeriods}
            >
              {loadingPeriods ? 'Retrying…' : 'Retry'}
            </Button>
          )}
        >
          <AlertTitle>Cannot list the months</AlertTitle>
          {periodsError} Reading the history still works; starting a run does not.
        </Alert>
      )}

      {pollIssue && (
        <Alert
          severity="error"
          action={(
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setPollPaused(false);
                  setPollIssue(null);
                }}
              >
                Retry status
              </Button>
              <Button color="inherit" size="small" onClick={handleAbandonWait}>
                Abandon wait
              </Button>
            </Stack>
          )}
        >
          <AlertTitle>
            {pollIssue.mayBeActive === false ? 'This run is not running' : 'Run status unavailable'}
          </AlertTitle>
          {pollIssue.message}
          {pollIssue.mayBeActive === false
            ? ' Abandon the wait to start another; nothing it wrote is lost.'
            : ' The run may still be active.'}
        </Alert>
      )}

      <Box
        data-testid="remittance-compact-selectors"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
          gridTemplateAreas: REMITTANCE_SELECTOR_GRID_AREAS,
          columnGap: 1.5,
          // Empty disclosure rows have zero height; a row gap would still leave blank space.
          rowGap: 0,
          alignItems: 'flex-start',
        }}
      >
        {/*
          The selectors share one compact row, while each independently opened body spans both
          columns. Named grid rows keep Servicer files above Reconciliation runs when both are open.
        */}
        <RemittanceUploadPanel
          periods={periods}
          // Only when nothing is in flight: moving the run selector under a running month would
          // change which month the controls describe while they are disabled for another one.
          onStored={(uploaded) => loadPeriods(pending ? undefined : uploaded)}
          height={Math.max(Math.round(height * 0.22), 180)}
        />
        <RunHistoryGrid
          runs={runs}
          isLoading={loadingRuns}
          onSelectRun={setSelected}
          selectedRunId={runId}
          height={Math.max(Math.round(height * 0.22), 180)}
        />
      </Box>

      {/*
        Start a run. Above the history rather than inside RunHistoryGrid's toolbar, so it reads as
        "do this to the month" rather than "do this to the selected row" — the month being run has
        nothing to do with whichever run is selected below.
      */}
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="remittance-period-label">Month</InputLabel>
          <Select
            labelId="remittance-period-label"
            label="Month"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            disabled={!!pending || loadingPeriods || !periods.length}
            inputProps={{ 'data-testid': 'remittance-period-select' }}
          >
            {periods.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="small"
          onClick={handleRun}
          disabled={!!RUN_UNAVAILABLE || !canRun || !period || !!pending}
          data-testid="remittance-run-button"
          startIcon={pending ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {pending ? `Running ${pending.period}…` : 'Run reconciliation'}
        </Button>
        {pending && (
          <Typography variant="caption" color="text.secondary">
            The engine records the result itself; this tab is waiting for it to appear.
          </Typography>
        )}
        {!canRun && (
          <Typography variant="caption" color="text.secondary">
            Running a reconciliation requires recon.remittance:run.
          </Typography>
        )}
        {RUN_UNAVAILABLE && (
          <Typography variant="caption" color="text.secondary" data-testid="remittance-run-unavailable">
            {RUN_UNAVAILABLE}
          </Typography>
        )}
      </Stack>

      {integrity && !integrity.clean && (
        <Alert severity="warning">
          <AlertTitle>Integrity — this run dropped input</AlertTitle>
          {integrity.unrecognized > 0 && (
            <div>
              {integrity.unrecognized} servicer workbook(s) were <strong>not recognized</strong> and
              contributed nothing. Money in an unread file produces no difference at all.
            </div>
          )}
          {integrity.unmatched > 0 && (
            <ExcludedInputs key={selected.RUN_ID} run={selected} />
          )}
        </Alert>
      )}

      <ReconciliationGrid
        loans={loans}
        isLoading={loadingLoans}
        period={selected?.PERIOD}
        height={Math.max(Math.round(height * 0.55), 320)}
      />
    </Box>
  );
};

export default RemittanceDashboard;
